import { useState } from 'react';
import { Link } from 'react-router-dom';
import NoDataAlert from '../../components/NoDataAlert';
import { formatDate } from '../../../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../portal/context/auth-context';
import TableSortButton from '../order-history/TableSortButton';
import {
    // leave all the below unused imports & const states in here for now
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    PrinterIcon,
    ShoppingCartIcon,
} from '@heroicons/react/24/outline';
export default function RFQTable({
    data,
    gettingNextPage,
    handleQuoteClick,
    hasSearched,
    handleFilterSubmit,
    arrowDirections,
    setArrowDirections,
    fetchdata,
}) {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [hoveredQuote, setHoveredQuote] = useState(null);
    const [hoveredCheckout, setHoveredCheckout] = useState(null);
    const [expandedQuoteIds, setExpandedQuoteIds] = useState(() => new Set());

    const setPartNumbersExpanded = (quoteId, isOpen) => {
        setExpandedQuoteIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (isOpen) {
                nextIds.add(quoteId);
            } else {
                nextIds.delete(quoteId);
            }
            return nextIds;
        });
    };

    // Ensure that 'quotes' is always an array.
    // If 'data.quote' exists and is an array, assign it to 'quotes', otherwise use an empty array.
    //RFQTable never crashes, even when switched accounts have zero RFQs.
    const quotes = Array.isArray(data?.quote) ? data.quote : [];

    const pushToCheckout = (quoteId) => {
        currentUser.order = null;
        navigate(`/checkout/?quote=${quoteId}`);
    };

    //You avoid all null/undefined errors and the UI shows the correct “No Data” screen.
    if (!gettingNextPage && quotes.length === 0) {
        return (
            <div className='flex flex-col'>
                <NoDataAlert
                    title={'Quotes'}
                    text={'Please note quotes are only valid for 30 days and will not appear if after this.'}
                />
            </div>
        );
    }

    return (
        <div className='px-4 -mt-4 sm:px-6 lg:px-8'>
            <div className='mx-auto mt-6 transition-all'>
                <table className='w-full table-auto'>
                    <thead>
                        <tr className='text-white bg-blue-primary'>
                            <th scope='col' className='px-3 py-2 w-36 font-semibold'>
                                <div className='flex flex-row justify-between'>
                                    <div>
                                        <p>Quote Number</p>
                                    </div>
                                    <TableSortButton
                                        fetchdata={handleFilterSubmit}
                                        arrowDirections={arrowDirections}
                                        setArrowDirections={setArrowDirections}
                                        name={'quoteNumber'}
                                    />
                                </div>
                            </th>
                            <th scope='col' className='px-3 py-3 w-48 font-normal'>
                                <div className='flex flex-row justify-between'>
                                    <div>
                                        <p>Date</p>
                                    </div>
                                    <TableSortButton
                                        fetchdata={handleFilterSubmit}
                                        arrowDirections={arrowDirections}
                                        setArrowDirections={setArrowDirections}
                                        name={'createdDate'}
                                    />
                                </div>
                            </th>
                            <th scope='col' className='px-3 py-3 font-normal text-left'>
                                Part Numbers {hasSearched && ' (Filtered)'}
                            </th>
                            <th scope='col' className='px-3 py-3 w-48 font-normal text-left'>
                                {hasSearched && ' (Filtered)'}
                                <div className='flex flex-row justify-between'>
                                    <div>
                                        <p>Total</p>
                                    </div>
                                    <TableSortButton
                                        fetchdata={handleFilterSubmit}
                                        arrowDirections={arrowDirections}
                                        setArrowDirections={setArrowDirections}
                                        name={'quoteTotal'}
                                    />
                                </div>
                            </th>
                            <th scope='col' className='px-3 py-3 w-36 font-normal text-right'>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    {!gettingNextPage ? (
                        <tbody className='bg-white'>
                            {/* Handles all state and rendering issues related to switching accounts, sorting, printing, and empty states, preventing common errors like "Cannot read properties", disappearing RFQs, and sort errors. */}
                            {quotes.map((rfqLine, personIdx) => {
                                const partNumbersAreLong = rfqLine.partNumbers.length > 60;
                                const showFullPartNumbers = !partNumbersAreLong || expandedQuoteIds.has(rfqLine.quoteId);

                                return (
                                    <tr key={rfqLine.Name} className={personIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
                                        <td className='px-4 py-2 font-semibold border'> {rfqLine.Name}</td>
                                        <td className='px-4 py-2 border'>
                                            {' '}
                                            {rfqLine.inscor__Create_Date__c
                                                ? formatDate(rfqLine.inscor__Create_Date__c)
                                                : null}
                                        </td>
                                        <td className='relative px-4 py-2 border'>
                                            <div className='flex justify-start pr-4'>
                                                {showFullPartNumbers ? (
                                                    <div className='w-full'>{rfqLine.partNumbers}</div>
                                                ) : (
                                                    <div className='w-full'>
                                                        {rfqLine.partNumbers.slice(0, 60)}
                                                        <span className='cursor-pointer text-blue-primary'>...</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                {!showFullPartNumbers && (
                                                    <ArrowsPointingOutIcon
                                                        onClick={() => setPartNumbersExpanded(rfqLine.quoteId, true)}
                                                        className='absolute top-2 right-2 w-5 h-5 cursor-pointer text-blue-primary hover:text-blue-secondary'
                                                    />
                                                )}
                                                {showFullPartNumbers && partNumbersAreLong && (
                                                    <ArrowsPointingInIcon
                                                        onClick={() => setPartNumbersExpanded(rfqLine.quoteId, false)}
                                                        className='absolute top-2 right-2 w-5 h-5 cursor-pointer text-blue-primary hover:text-blue-secondary'
                                                    />
                                                )}
                                            </div>
                                        </td>

                                        <td className='px-4 py-2 border'>
                                            <div className='flex justify-end'>{rfqLine.inscor__Quote_Total__c}</div>
                                        </td>
                                        <td className='px-4 py-2 border'>
                                            <div className='flex justify-end items-center space-x-2'>
                                                <div
                                                    onClick={() => {
                                                        handleQuoteClick(rfqLine.Id, rfqLine.quoteNumber);
                                                        fetchdata(true);
                                                    }}
                                                    onMouseEnter={() => {
                                                        setHoveredQuote(rfqLine.Id);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredQuote(null);
                                                    }}
                                                >
                                                    <PrinterIcon className='w-6 h-6 text-gray-800 cursor-pointer hover:text-gray-400' />

                                                    {hoveredQuote === rfqLine.Id && (
                                                        <div className='absolute z-50 p-2 bg-white rounded-lg shadow-lg'>
                                                            <div className='flex flex-col space-y-2'>
                                                                <div className='flex justify-between items-center'>
                                                                    <div className='text-sm font-semibold'>Print Quote</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div
                                                    onMouseEnter={() => {
                                                        setHoveredCheckout(rfqLine.Id);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredCheckout(null);
                                                    }}
                                                >
                                                    <Link
                                                        onClick={() => {
                                                            currentUser.order = null;
                                                        }}
                                                        to={`/?quoteId=${rfqLine.Id}`}
                                                    >
                                                        <ShoppingCartIcon className='w-6 h-6 text-gray-800 cursor-pointer hover:text-gray-400' />
                                                    </Link>
                                                    {hoveredCheckout === rfqLine.Id && (
                                                        <div className='absolute z-50 p-2 bg-white rounded-lg shadow-lg'>
                                                            <div className='flex flex-col space-y-2'>
                                                                <div className='flex justify-between items-center'>
                                                                    <div className='text-sm font-semibold'>
                                                                        Edit or Buy Parts
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    ) : null}
                </table>

                {gettingNextPage && (
                    <div>
                        <div className='flex justify-center py-4 w-full border'>
                            <svg
                                className='mr-3 -ml-1 w-5 h-5 animate-spin text-blue-primary'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    className='opacity-75'
                                    fill='currentColor'
                                    d='M4 12a8 8 0 018-8v1a7 7 0 00-7 7h1z'
                                ></path>
                                <circle
                                    className='opacity-25'
                                    cx='12'
                                    cy='12'
                                    r='10'
                                    stroke='currentColor'
                                    strokeWidth='4'
                                ></circle>
                            </svg>
                            <span className='text-blue-primary'>Loading...</span>
                        </div>
                        <div>
                            <div className='mb-14 h-80'></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
